import React, { useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbDrawer,
  WxbModal,
  WxbSegmented,
  WxbTableActionCell,
  WxbTag,
} from '../wxb-ui';
import EquipmentActionModal from './EquipmentActionModal';
import EquipmentAttributeModal from './EquipmentAttributeModal';
import CyclePolicyModal from './CyclePolicyModal';
import HygienePathEditor from './HygienePathEditor';
import TypedConditionEditor from './TypedConditionEditor';
import { displayAttributeCode, isAutoAttributeCode } from './attributeCodeGeneration';
import {
  ACTION_ORIGIN_LABELS,
  ATTRIBUTE_KIND_LABELS,
  EFFECT_TIMING_LABELS,
  cloneEquipmentStateModel,
  deriveTransitionPreviews,
  formatCondition,
  formatEffect,
  formatTemporalExpression,
  normalizeLegacyTemporalDraft,
  validateEquipmentStateModelLocally,
} from './equipmentStateCatalogModel';
import type {
  EquipmentStateAction,
  EquipmentStateAttribute,
  EquipmentStateClass,
  EquipmentStateClassModel,
  EquipmentStateTemporalTransition,
} from '../../types/prodEquipmentStateCatalog';
import type {
  EquipmentStateTransitionPreview,
} from './equipmentStateCatalogModel';

export type EquipmentStateModelEditorView = 'hygiene' | 'definitions' | 'actions' | 'transitions' | 'ready';

const temporalTransitionOf = (attribute: EquipmentStateAttribute): EquipmentStateTemporalTransition | null => (
  attribute.temporalTransition
);

const hasTemporalTransition = (attribute: EquipmentStateAttribute): boolean => Boolean(temporalTransitionOf(attribute));

const temporalTransitionSummary = (attribute: EquipmentStateAttribute): string | null => {
  const timeDriven = attribute.timeDrivenTransitions ?? [];
  if (timeDriven.length > 0) {
    return timeDriven
      .map((transition) => `${transition.sourceValueCode} → ${transition.targetValueCode} · ${formatTemporalExpression(transition.expiryRule.expression)}`)
      .join('；');
  }
  const transition = temporalTransitionOf(attribute);
  if (!transition) return null;
  return `有效：${transition.validValueCode} → 到期：${transition.expiredValueCode} · ${formatTemporalExpression(transition.expiryRule.expression)}`;
};

interface Props {
  open: boolean;
  equipmentClass: EquipmentStateClass | null;
  readOnly: boolean;
  saving: boolean;
  activeView: EquipmentStateModelEditorView;
  onActiveViewChange: (view: EquipmentStateModelEditorView) => void;
  onClose: () => void;
  onSave: (model: EquipmentStateClassModel) => Promise<boolean>;
}

const clockSummary = (attribute: EquipmentStateAttribute): string => {
  if (attribute.kind === 'CLOCK') return `${attribute.clockKind ?? '—'} · ${formatTemporalExpression(attribute.temporalExpression)}`;
  if (attribute.kind === 'COUNTER') {
    const range = attribute.minimum == null && attribute.maximum == null
      ? ''
      : ` · ${attribute.minimum ?? '−∞'} ~ ${attribute.maximum ?? '+∞'}`;
    return `${attribute.initialNumber ?? '—'} ${attribute.unit ?? ''}${range}`;
  }
  return attribute.values.length > 0
    ? attribute.values.map((value) => `${value.name} (${value.code})`).join('、')
    : '尚无状态值';
};

const EquipmentStateModelDrawer: React.FC<Props> = ({
  open,
  equipmentClass,
  readOnly,
  saving,
  activeView,
  onActiveViewChange,
  onClose,
  onSave,
}) => {
  const [model, setModel] = useState<EquipmentStateClassModel | null>(null);
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [attributeModalOpen, setAttributeModalOpen] = useState(false);
  const [editingAttributeIndex, setEditingAttributeIndex] = useState<number | null>(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [editingActionIndex, setEditingActionIndex] = useState<number | null>(null);
  const [cyclePolicyModalOpen, setCyclePolicyModalOpen] = useState(false);
  const [editingCyclePolicyIndex, setEditingCyclePolicyIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !equipmentClass) return;
    setModel(cloneEquipmentStateModel(equipmentClass.model));
    setDirty(false);
    setDiscardOpen(false);
  }, [equipmentClass, open]);

  const preparedModel = useMemo(() => (
    model && !readOnly ? normalizeLegacyTemporalDraft(model) : model
  ), [model, readOnly]);
  const problems = useMemo(() => (
    preparedModel ? validateEquipmentStateModelLocally(preparedModel) : []
  ), [preparedModel]);
  const transitionPreviews = useMemo(() => model ? deriveTransitionPreviews(model) : [], [model]);
  const temporalTransitionCount = useMemo(() => (
    model?.attributes.filter(hasTemporalTransition).length ?? 0
  ), [model]);
  const timeDrivenTransitionCount = useMemo(() => (
    model?.attributes.reduce((count, attribute) => count + (attribute.timeDrivenTransitions?.length ?? 0), 0) ?? 0
  ), [model]);

  const requestClose = () => {
    if (dirty && !readOnly) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  };

  const updateModel = (next: EquipmentStateClassModel) => {
    setModel(next);
    setDirty(true);
  };

  const save = async () => {
    if (!preparedModel || problems.length > 0) return;
    const saved = await onSave(preparedModel);
    if (saved) {
      setModel(preparedModel);
      setDirty(false);
    }
  };

  const attributeColumns: ColumnsType<EquipmentStateAttribute> = [
    {
      title: '属性',
      key: 'attribute',
      render: (_, attribute) => (
        <div>
          <div className="esc-table-primary">{attribute.name}</div>
          {isAutoAttributeCode(attribute.code) ? (
            <WxbTag color="amber">保存后系统生成</WxbTag>
          ) : (
            <code className="esc-table-code">{attribute.code}</code>
          )}
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'kind',
      width: 130,
      render: (kind: EquipmentStateAttribute['kind'], attribute: EquipmentStateAttribute) => {
        if (kind === 'DISCRETE' && hasTemporalTransition(attribute)) {
          return <WxbTag color="amber">周期状态（离散）</WxbTag>;
        }
        return (
          <WxbTag color={kind === 'DISCRETE' ? 'blue' : kind === 'COUNTER' ? 'green' : 'amber'}>
            {ATTRIBUTE_KIND_LABELS[kind]}
          </WxbTag>
        );
      },
    },
    {
      title: '定义',
      key: 'definition',
      render: (_, attribute) => temporalTransitionSummary(attribute) ?? clockSummary(attribute),
    },
    {
      title: '操作',
      key: 'actions',
      width: 190,
      render: (_, attribute) => {
        const index = model?.attributes.indexOf(attribute) ?? -1;
        return (
          <WxbTableActionCell
            actions={[
              {
                key: 'edit',
                label: readOnly ? '查看' : '编辑',
                onClick: () => {
                  setEditingAttributeIndex(index);
                  setAttributeModalOpen(true);
                },
              },
              {
                key: 'delete',
                label: '删除',
                variant: 'danger',
                disabled: readOnly,
                confirm: {
                  title: `删除属性“${attribute.name}”？`,
                  description: '引用该属性的守卫、效果与就绪条件也会一并移除。',
                  okText: '删除',
                },
                onClick: () => {
                  if (!model || index < 0) return;
                  updateModel({
                    attributes: model.attributes.filter((_, itemIndex) => itemIndex !== index),
                    actions: model.actions.map((action) => ({
                      ...action,
                      guards: action.guards.filter((guard) => guard.attributeCode !== attribute.code),
                      effects: action.effects.filter((effect) => effect.attributeCode !== attribute.code),
                    })),
                    readyConditions: model.readyConditions.filter((condition) => (
                      condition.attributeCode !== attribute.code
                    )),
                    cyclePolicies: model.cyclePolicies,
                  });
                },
              },
            ]}
          />
        );
      },
    },
  ];

  const actionColumns: ColumnsType<EquipmentStateAction> = [
    {
      title: '动作',
      key: 'action',
      render: (_, action) => (
        <div>
          <div className="esc-table-primary">{action.name}</div>
          <code className="esc-table-code">{action.actionCode}</code>
        </div>
      ),
    },
    {
      title: '关联操作',
      key: 'operation',
      render: (_, action) => action.operationId ? (
        <div>
          <div className="esc-table-primary">已关联操作库</div>
          <code className="esc-table-code">#{action.operationId}</code>
        </div>
      ) : <WxbTag color="amber">历史未关联</WxbTag>,
    },
    {
      title: '计划态生效',
      dataIndex: 'effectTiming',
      width: 140,
      render: (effectTiming: EquipmentStateAction['effectTiming']) => (
        <WxbTag color={effectTiming === 'START' ? 'blue' : 'green'}>
          {EFFECT_TIMING_LABELS[effectTiming]}
        </WxbTag>
      ),
    },
    {
      title: '排产来源',
      dataIndex: 'origin',
      width: 125,
      render: (origin: EquipmentStateAction['origin']) => (
        <WxbTag color={origin === 'DERIVABLE' ? 'green' : 'blue'}>
          {ACTION_ORIGIN_LABELS[origin]}
        </WxbTag>
      ),
    },
    {
      title: '发生前状态条件（AND）',
      key: 'guards',
      render: (_, action) => action.guards.length > 0
        ? action.guards.map(formatCondition).join(' ∧ ')
        : '无前置条件',
    },
    {
      title: '发生后状态变化',
      key: 'effects',
      render: (_, action) => action.effects.map(formatEffect).join('；') || '—',
    },
    {
      title: '操作',
      key: 'actions',
      width: 175,
      render: (_, action, index) => (
        <WxbTableActionCell
          actions={[
            {
              key: 'edit',
              label: readOnly ? '查看' : '编辑',
              onClick: () => {
                setEditingActionIndex(index);
                setActionModalOpen(true);
              },
            },
            {
              key: 'delete',
              label: '删除',
              variant: 'danger',
              disabled: readOnly,
              confirm: {
                title: `删除动作“${action.name}”？`,
                description: `稳定动作编码 ${action.actionCode} 将从当前草稿移除。`,
                okText: '删除',
              },
              onClick: () => {
                if (!model) return;
                updateModel({ ...model, actions: model.actions.filter((_, itemIndex) => itemIndex !== index) });
              },
            },
          ]}
        />
      ),
    },
  ];

  const transitionColumns: ColumnsType<EquipmentStateTransitionPreview> = [
    {
      title: '属性',
      dataIndex: 'attributeCode',
      width: 150,
      render: (value: string) => <code className="esc-table-code">{displayAttributeCode(value)}</code>,
    },
    {
      title: '状态转移',
      key: 'transition',
      render: (_, row) => (
        <div className="esc-transition-path">
          <span>{row.from}</span>
          <span aria-hidden="true">→</span>
          <strong>{row.to}</strong>
        </div>
      ),
    },
    {
      title: '动作',
      key: 'action',
      render: (_, row) => (
        <div>
          <div className="esc-table-primary">{row.actionName}</div>
          <code className="esc-table-code">{row.actionCode}</code>
          <div className="esc-transition-context">{EFFECT_TIMING_LABELS[row.effectTiming]}</div>
        </div>
      ),
    },
    {
      title: '来源',
      dataIndex: 'origin',
      width: 125,
      render: (origin: EquipmentStateAction['origin']) => (
        <WxbTag color={origin === 'DERIVABLE' ? 'green' : 'blue'}>
          {ACTION_ORIGIN_LABELS[origin]}
        </WxbTag>
      ),
    },
    {
      title: '跨属性守卫 / 其他效果',
      key: 'context',
      render: (_, row) => (
        <div className="esc-transition-context">
          <span>{row.crossAttributeGuards.join(' ∧ ') || '无跨属性守卫'}</span>
          <span>{row.otherEffects.join('；') || '无其他效果'}</span>
        </div>
      ),
    },
  ];

  const body = !equipmentClass || !model ? null : (
    <div className="esc-model-drawer-body">
      <div className="esc-model-context">
        <div>
          <span className="esc-model-context-label">设备类别</span>
          <strong>{equipmentClass.name}</strong>
          <code>{equipmentClass.code}</code>
        </div>
        <div className="esc-model-metrics">
          <span>{model.attributes.length} 状态定义</span>
          <span>{temporalTransitionCount} 两态周期</span>
          <span>{timeDrivenTransitionCount} 自动到期边</span>
          <span>{model.actions.length} 动作</span>
          <span>{model.readyConditions.length} 就绪条件</span>
        </div>
      </div>

      {readOnly && (
        <WxbAlert title="当前版本只读">
          已发布、已取代或已放弃的目录只能查看；请从历史版本新建草稿后修改。
        </WxbAlert>
      )}
      {problems.length > 0 && !readOnly && (
        <WxbAlert variant="error" title={`本地检查发现 ${problems.length} 个问题`}>
          <ul className="esc-problem-list">
            {problems.map((problem) => <li key={problem.key}>{problem.message}</li>)}
          </ul>
        </WxbAlert>
      )}

      <WxbSegmented
        value={activeView}
        options={[
          { value: 'hygiene', label: '清洗灭菌路径' },
          { value: 'definitions', label: `状态与周期定义 (${model.attributes.length})` },
          { value: 'actions', label: `动作 (${model.actions.length})` },
          { value: 'ready', label: `就绪条件 (${model.readyConditions.length})` },
          { value: 'transitions', label: `联动预览 (${transitionPreviews.length})` },
        ]}
        onChange={(value) => onActiveViewChange(value as EquipmentStateModelEditorView)}
      />

      {activeView === 'hygiene' && (
        <HygienePathEditor
          model={model}
          readOnly={readOnly}
          onChange={updateModel}
          onAddAttribute={() => {
            setEditingAttributeIndex(null);
            setAttributeModalOpen(true);
          }}
          onEditAttribute={(index) => {
            setEditingAttributeIndex(index);
            setAttributeModalOpen(true);
          }}
          onEditAction={(index) => {
            setEditingActionIndex(index >= 0 ? index : null);
            setActionModalOpen(true);
          }}
          onAddCyclePolicy={() => {
            setEditingCyclePolicyIndex(null);
            setCyclePolicyModalOpen(true);
          }}
          onEditCyclePolicy={(index) => {
            setEditingCyclePolicyIndex(index);
            setCyclePolicyModalOpen(true);
          }}
        />
      )}

      {activeView === 'definitions' && (
        <div className="esc-editor-panel">
          <div className="esc-editor-panel-head">
            <div>
              <h3>状态与周期定义</h3>
              <p>维护原始状态、计数和时钟定义。多状态清洗灭菌有效期请优先在“清洗灭菌路径”中维护；两态周期状态仅为历史兼容配置。</p>
            </div>
            <WxbButton
              type="button"
              size="sm"
              variant="secondary"
              disabled={readOnly}
              onClick={() => {
                setEditingAttributeIndex(null);
                setAttributeModalOpen(true);
              }}
            >
              添加状态定义
            </WxbButton>
          </div>
          <WxbDataTable<EquipmentStateAttribute>
            rowKey="code"
            columns={attributeColumns}
            dataSource={model.attributes}
            pagination={false}
            density="compact"
            emptyState={{ description: '尚未定义状态或周期定义。请先添加离散状态、计数或期限定义。' }}
          />
        </div>
      )}

      {activeView === 'actions' && (
        <div className="esc-editor-panel">
          <div className="esc-editor-panel-head">
            <div>
              <h3>稳定动作</h3>
              <p>每个状态动作关联操作库中的一条操作；APS 按其开始或结束时点投影未来状态。</p>
            </div>
            <WxbButton
              type="button"
              size="sm"
              variant="secondary"
              disabled={readOnly || model.attributes.length === 0}
              onClick={() => {
                setEditingActionIndex(null);
                setActionModalOpen(true);
              }}
            >
              添加动作
            </WxbButton>
          </div>
          <WxbDataTable<EquipmentStateAction>
            rowKey="actionCode"
            columns={actionColumns}
            dataSource={model.actions}
            pagination={false}
            density="compact"
            emptyState={{ description: model.attributes.length === 0 ? '请先定义状态属性。' : '尚未定义状态动作。' }}
          />
        </div>
      )}

      {activeView === 'ready' && (
        <div className="esc-editor-panel">
          <div className="esc-editor-panel-head">
            <div>
              <h3>类别就绪条件</h3>
              <p>所有条件按 AND 组合，是唯一的设备类别 readiness 定义。</p>
            </div>
          </div>
          <TypedConditionEditor
            attributes={model.attributes}
            conditions={model.readyConditions}
            disabled={readOnly}
            emptyText="尚未定义就绪条件；发布校验将给出明确问题。"
            onChange={(readyConditions) => updateModel({ ...model, readyConditions })}
          />
          {model.readyConditions.length > 0 && (
            <div className="esc-ready-preview">
              <span>ready :=</span>
              <code>{model.readyConditions.map(formatCondition).join(' ∧ ')}</code>
            </div>
          )}
        </div>
      )}

      {activeView === 'transitions' && (
        <div className="esc-editor-panel">
          <div className="esc-editor-panel-head">
            <div>
              <h3>模型联动预览</h3>
              <p>只读派生属性状态转移；不生成运行实例或第二份真值。</p>
            </div>
          </div>
          <div className="esc-subsection-header">
            <div>
              <h3>属性状态转移</h3>
              <p>由发生后状态变化和同属性发生前条件实时推导。</p>
            </div>
          </div>
          <WxbDataTable<EquipmentStateTransitionPreview>
            rowKey="key"
            columns={transitionColumns}
            dataSource={transitionPreviews}
            pagination={false}
            density="compact"
            emptyState={{ description: '当前动作中没有可推导的离散状态转移。' }}
          />
        </div>
      )}

    </div>
  );

  return (
    <>
      <WxbDrawer
        open={open}
        title="设备类别状态模型"
        width={1080}
        maskClosable={!dirty || readOnly}
        onClose={requestClose}
        extra={(
          <div className="esc-drawer-actions">
            <WxbButton type="button" variant="ghost" onClick={requestClose}>关闭</WxbButton>
            {!readOnly && (
              <WxbButton
                type="button"
                disabled={!dirty || saving || problems.length > 0}
                onClick={save}
              >
                {saving ? '保存中...' : '保存类别模型'}
              </WxbButton>
            )}
          </div>
        )}
      >
        {body}
      </WxbDrawer>

      {model && (
        <>
          <EquipmentAttributeModal
            open={attributeModalOpen}
            readOnly={readOnly}
            initialAttribute={editingAttributeIndex == null ? null : model.attributes[editingAttributeIndex]}
            existingCodes={model.attributes.map((attribute) => attribute.code)}
            attributes={model.attributes}
            onCancel={() => setAttributeModalOpen(false)}
            onSave={(attribute) => {
              const previousAttribute = editingAttributeIndex == null
                ? null
                : model.attributes[editingAttributeIndex];
              const normalizedDraft = previousAttribute
                ? normalizeLegacyTemporalDraft(model, [previousAttribute.code])
                : model;
              const attributes = editingAttributeIndex == null
                ? [...normalizedDraft.attributes, attribute]
                : normalizedDraft.attributes.map((item, index) => index === editingAttributeIndex ? attribute : item);
              updateModel({ ...normalizedDraft, attributes });
              setAttributeModalOpen(false);
            }}
          />
          <EquipmentActionModal
            open={actionModalOpen}
            readOnly={readOnly}
            initialAction={editingActionIndex == null ? null : model.actions[editingActionIndex]}
            existingCodes={model.actions.map((action) => action.actionCode)}
            attributes={model.attributes}
            onCancel={() => setActionModalOpen(false)}
            onSave={(action) => {
              const actions = editingActionIndex == null
                ? [...model.actions, action]
                : model.actions.map((item, index) => index === editingActionIndex ? action : item);
              updateModel({ ...model, actions });
              setActionModalOpen(false);
            }}
          />
          <CyclePolicyModal
            open={cyclePolicyModalOpen}
            readOnly={readOnly}
            initialPolicy={editingCyclePolicyIndex == null ? null : model.cyclePolicies[editingCyclePolicyIndex]}
            existingCodes={model.cyclePolicies.map((policy) => policy.code)}
            attributes={model.attributes}
            actions={model.actions}
            onCancel={() => setCyclePolicyModalOpen(false)}
            onSave={(policy) => {
              const cyclePolicies = editingCyclePolicyIndex == null
                ? [...model.cyclePolicies, policy]
                : model.cyclePolicies.map((item, index) => index === editingCyclePolicyIndex ? policy : item);
              updateModel({ ...model, cyclePolicies });
              setCyclePolicyModalOpen(false);
            }}
          />
        </>
      )}

      <WxbModal
        open={discardOpen}
        title="放弃未保存修改？"
        okText="放弃修改"
        okVariant="danger"
        onCancel={() => setDiscardOpen(false)}
        onOk={() => {
          setDiscardOpen(false);
          onClose();
        }}
      >
        当前类别的状态与周期定义、动作或就绪条件尚未保存，关闭后无法恢复。
      </WxbModal>
    </>
  );
};

export default EquipmentStateModelDrawer;
